import { useEffect, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { v4 as uuid } from 'uuid';

import { BaseApiStore } from '../../../store/BaseApiStore.ts';
import { ToastManager } from '../../../ToastManager';

import { useAttach } from '../../../store/useAttach';

import { useFlowEditor } from './useFlowEditor';

export class FlowTest extends BaseApiStore {
  constructor() {
    super('flowTest');
  }

  createInitialState() {
    return {
      sessionId: `mha-${uuid()}`,
      data: null,
    };
  }

  // This assumes manager Flow is connected. This is true since we are on the Flow page.
  onAttached({ api }) {
    api.flow.on('testFlowCardStart', this.handleTestFlowCardStart);
    api.flow.on('testFlowCardError', this.handleTestFlowCardError);
    api.flow.on('testFlowCardEnd', this.handleTestFlowCardEnd);
  }

  onDetached({ api }) {
    api.flow.off('testFlowCardStart', this.handleTestFlowCardStart);
    api.flow.off('testFlowCardError', this.handleTestFlowCardError);
    api.flow.off('testFlowCardEnd', this.handleTestFlowCardEnd);
  }

  handleTestFlowCardStart = (card) => {
    const state = this.get();
    if (card.sessionId === state.sessionId) {
      const type = card.type.replace('flowcard', '');

      this.set({
        data: {
          ...state.data,
          [`${type}::${card.index}`]: {
            ...card,
            state: 'start',
          },
        },
      });
    }
  };

  handleTestFlowCardError = (card) => {
    const state = this.get();

    if (card.sessionId === state.sessionId) {
      const type = card.type.replace('flowcard', '');

      this.set({
        data: {
          ...state.data,
          [`${type}::${card.index}`]: {
            ...card,
            state: 'error',
          },
        },
      });

      console.error(card.err);
      ToastManager.addError({
        message: card.err,
      });
    }
  };

  handleTestFlowCardEnd = (card) => {
    const state = this.get();
    if (card.sessionId === state.sessionId) {
      // card.sessionId === state.sessionId
      const type = card.type.replace('flowcard', '');

      this.set({
        data: {
          ...state.data,
          [`${type}::${card.index}`]: {
            ...card,
            state: 'end',
          },
        },
      });
    }
  };

  setFlow({ flow }) {
    this.set({ flow: flow, data: null });
  }

  async testFlow({ tokens = {} } = {}) {
    const state = this.get();
    this.set({ data: null });

    const flow = {
      trigger: state.flow.trigger,
      conditions: state.flow.conditions,
      actions: state.flow.actions,
    };

    const hasName = state.flow.name?.length > 0 ?? false;
    flow.name = hasName ? state.flow.name : 'Test';

    await this.api.flow.testFlow({
      sessionId: state.sessionId,
      tokens: tokens,
      flow: flow,
    });
  }
}

export const flowTest = new FlowTest();

export function useFlowTestListener() {
  useAttach(flowTest, 'flowTest');

  const { flow } = useFlowEditor();

  useEffect(() => {
    flowTest.setFlow({ flow });
  }, [flow]);
}

export function useFlowTest({ cardType, cardIndex }) {
  useAttach(flowTest, 'flowTest');

  const selector = useCallback(
    (state) => state.data?.[`${cardType}::${cardIndex}`] ?? null,
    [cardType, cardIndex]
  );

  return flowTest.store(selector, shallow);
}
